import type {Delimiter, Html, Root, Text} from 'mdast'
import type {Extension} from 'mdast-util-from-markdown'
import {visitParents} from 'unist-util-visit-parents'

import {tokenizeHtml} from '../../html/tokenize-html'
import {addPoints} from '../../syntax-tree/node-range-utils'
import {isPositionedNode} from '../../syntax-tree/positioned-node'
import type {ParentsOf} from '../../syntax-tree/type-utils'

/** Split a single node, returning an array of new nodes. */
function splitHtmlNodeByTokens(node: Html) {
  if (!isPositionedNode(node)) return []

  return tokenizeHtml(node.value).flatMap((token): Array<Html | Text | Delimiter> => {
    const value = node.value.slice(token.position.start.offset, token.position.end.offset)

    // Token positions will be relative to the node value; we need to add the node start point to get a document position
    const position = {
      start: addPoints(node.position.start, token.position.start),
      end: addPoints(node.position.start, token.position.end),
    }

    switch (token.type) {
      case 'text':
        return [{type: 'text', value, position}]
      case 'endTag':
        // End tag delimiters are pushed in front of the tag so that the delimiter ends up inside the resulting element
        return [
          {type: 'delimiter', tokenType: 'htmlTag', raw: value, position},
          {type: 'html', value, position},
        ]
      case 'startTag':
        return [
          {type: 'html', value, position},
          {type: 'delimiter', tokenType: 'htmlTag', raw: value, position},
        ]
      case 'comment':
      case 'doctype':
        // Comments and doctypes don't need delimiters because we don't render them
        return [{type: 'html', value, position}]
    }
  })
}

/** Split all the HTML nodes in the tree, replacing them with their split nodes. Mutates the tree. */
function splitHtmlNodes(tree: Root) {
  visitParents(
    tree,
    'html',
    (node, ancestors) => {
      // Replace this node with split nodes
      const parent = ancestors.at(-1) as ParentsOf<Html>
      const index = parent.children.indexOf(node)
      parent.children.splice(index, 1, ...splitHtmlNodeByTokens(node))
    },
    // Traversing in reverse ensures that we don't re-encounter the new nodes that we inserted
    true,
  )
}

/**
 * By default, mdast will output one `html` node for each 'chunk' of HTML. For example, the text
 * `<h1><span>Hello world</span>` would be represented as a single `html` node:
 *
 * ```
 * root[1] (1:1-1:29, 0-28)
 * └─0 html "<h1><span>Hello world</span>" (1:1-1:29, 0-28)
 * ```
 *
 * This extension splits all `html` nodes apart by tag, injects delimiter nodes for the tags, and replaces any
 * remaining text with `text` nodes. So the above example would become seven separate nodes:
 *
 * ```
 * root[7] (1:1-1:29, 0-28)
 * ├─0 html "<h1>" (1:1-1:5, 0-4)
 * ├─1 delimiter (1:1-1:5, 0-4)
 * │     tokenType: "startTag"
 * │     raw: "<h1>"
 * ├─2 html "<span>" (1:5-1:11, 4-10)
 * ├─3 delimiter (1:5-1:11, 4-10)
 * │     tokenType: "startTag"
 * │     raw: "<span>"
 * ├─4 text "Hello world" (1:11-1:22, 10-21)
 * ├─5 delimiter (1:22-1:29, 21-28)
 * │     tokenType: "endTag"
 * │     raw: "</span>"
 * └─6 html "</span>" (1:22-1:29, 21-28)
 * ```
 */
export function mdastSplitHtml(): Extension {
  return {
    transforms: [splitHtmlNodes],
  }
}
